import { isAfter, isWithinInterval, parse } from "date-fns";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetPageByPathQuery } from "../services/wp";
import promiseAllSequential from "../utils/promiseAllSequential";
import _ from "lodash";
import { addToCart } from "../actions/cart";

const useTicketsForPath = (
    input:
        | { preview?: false; path: string }
        | { preview: true; page_id: string }
) => {
    // What is this for
    const eligibleUserTypes = ["staff", "student", "alumni", "college"];

    const { profile, loggedIn } = useSelector((state: any): any => state.user);
    const cart = useSelector((state: any): any => state.cart);
    const dispatch = useDispatch();

    // This should probably be in redux
    const [quantities, setQuantities] = useState<Record<string, number>>({});

    const getQuantity = (ticketId: number) => quantities[ticketId] ?? 0;
    const getTotalQuantity = () =>
        Object.values(quantities).reduce(
            (accumulator, next) => accumulator + next,
            0
        );
    const incrementQuantity = (ticketId: number) =>
        setQuantities({
            ...quantities,
            [ticketId]: getQuantity(ticketId) + 1,
        });
    const decrementQuantity = (ticketId: number) =>
        setQuantities({
            ...quantities,
            [ticketId]: getQuantity(ticketId) - 1,
        });
    const resetQuantities = () => setQuantities({});

    const teamTicketFromCart = cart?.items?.find?.(
        cartItem =>
            cartItem.item_data &&
            cartItem.item_data[0] &&
            cartItem.item_data[0].team_name
    );

    const {
        additionalQuestions,
        tickets: ticketsRaw,
        selectedTeamTicketId,
        selectedTeamTicketSize,
        commentLabel,
    } = useGetPageByPathQuery(input, {
        selectFromResult: result => {
            const now = new Date();

            const selectedTeamTicket = result.data?.acf.event_products.find(
                eventProduct => {
                    return (
                        eventProduct.acf.product_behaviour === "team_ticket" &&
                        getQuantity(eventProduct.product.ID) > 0
                    );
                }
            );

            return {
                ...result,
                tickets: result.data?.acf.event_products
                    .map(eventProduct => {
                        const isInStock =
                            eventProduct.stock_status === "instock";
                        const stockQuantity = eventProduct.stock_quantity;
                        const availableFrom = parse(
                            eventProduct.acf.ticket_available_from,
                            "yyyy-MM-dd H:mm:ss",
                            new Date()
                        );
                        const availableUntil = parse(
                            eventProduct.acf.ticket_available_until,
                            "yyyy-MM-dd H:mm:ss",
                            new Date()
                        );

                        const isTeamTicket =
                            eventProduct.acf.product_behaviour ===
                            "team_ticket";

                        return {
                            ID: eventProduct.product.ID,
                            eligibility:
                                eventProduct.acf.eligible_for === "club_member"
                                    ? {
                                          for: "club_member" as const,
                                          clubId: eventProduct.acf
                                              .eligible_club,
                                      }
                                    : {
                                          for: eventProduct.acf.eligible_for,
                                      },
                            price: eventProduct.price,
                            isInStock,
                            stockQuantity,
                            maxQuantity: isTeamTicket
                                ? selectedTeamTicket || teamTicketFromCart
                                    ? 0
                                    : 1
                                : stockQuantity,
                            ticketName:
                                eventProduct.meta_data.find(
                                    ({ key }) => key === "crm_ticket_name"
                                )?.value ?? eventProduct.type,
                            type: eventProduct.type,
                            availableFrom,
                            availableUntil,
                            isAvailableForSale: isWithinInterval(now, {
                                start: availableFrom,
                                end: availableUntil,
                            }),
                            isTeamTicket,
                            isUtsIdRequired: eligibleUserTypes
                                .filter(userType => userType !== "alumni")
                                .includes(eventProduct.acf.eligible_for),
                        };
                    })
                    .filter(ticket => isAfter(now, ticket.availableFrom)),
                additionalQuestions:
                    result.data?.acf.ticket_additional_questions,
                selectedTeamTicketId: selectedTeamTicket?.product.ID,
                selectedTeamTicketSize: selectedTeamTicket?.acf.team_size,
                commentLabel: result.data?.acf.event_ticket_comment_label,
            };
        },
    });

    const tickets = ticketsRaw?.map(ticket => {
        const { eligibility } = ticket;
        if (eligibility.for === "public") {
            return {
                isUserEligible: true,
                eligibleAccountLabel: null,
                ...ticket,
            };
        }
        if (eligibility.for === "club_member") {
            const hasClubMembership = !!profile?.active_memberships?.some(
                membership => membership.club_id === eligibility.clubId
            );
            return {
                isUserEligible: hasClubMembership,
                eligibleAccountLabel: hasClubMembership ? "club member" : null,
                ...ticket,
            };
        }
        if (eligibleUserTypes.includes(eligibility.for as string)) {
            return {
                isUserEligible: profile?.type === eligibility.for,
                eligibleAccountLabel: `UTS ${eligibility.for}`,
                ...ticket,
            };
        }
        if (eligibility.for === "non_club_member") {
            return {
                isUserEligible: loggedIn,
                eligibleAccountLabel: null,
                ...ticket,
            };
        }
        return {
            isUserEligible: false,
            eligibleAccountLabel: null,
            ...ticket,
        };
    });

    const populateMissingFormData = (
        {
            free_text,
            dietary_requirements,
            tshirt_size,
            teammate_request,
            accessibility_requirements,
            emergency_contact,
            under_18,
            ...formData
        }: Record<string, string>,
        team = false
    ) => {
        const sanitizedData: Record<string, string> = {
            dietary_requirements: dietary_requirements ?? "",
            tshirt_size: tshirt_size ?? "",
            teammate_request: teammate_request ?? "",
            accessibility_requirements: accessibility_requirements ?? "",
            emergency_contact: emergency_contact ?? "",
            under_18: under_18 ?? "",
            ...formData,
        };
        if (!team) {
            sanitizedData.free_text = free_text ?? "";
        }

        return sanitizedData;
    };

    // TODO: This should be part of a new cart reducer so status can be tracked via redux
    const [isAddingTickets, setIsAddingTickets] = useState(false);
    const [isAddingTicketsSuccess, setIsAddingTicketsSuccess] = useState(false);
    const [addingTicketsError, setAddingTicketsError] = useState<string>();
    const addTicketsToCart = async (
        formData: Record<string, Record<string, string>[]>
    ) => {
        if (tickets == null || isAddingTickets) {
            return;
        }

        try {
            setIsAddingTickets(true);
            setIsAddingTicketsSuccess(false);
            setAddingTicketsError(undefined);

            const promises = Object.entries(quantities)
                .filter(([_, quantity]) => quantity > 0)
                .flatMap(([key, quantity]) => {
                    const ticketId = Number.parseInt(key);
                    const ticketData =
                        formData[ticketId] ??
                        Array.from({
                            length: selectedTeamTicketSize ?? 0,
                        }).map(() => ({} as Record<string, string>));
                    if (ticketId === selectedTeamTicketId) {
                        return () =>
                            dispatch(
                                addToCart(
                                    {
                                        id: ticketId,
                                        quantity: 1,
                                        cart_item_data: {
                                            item_data: [
                                                {
                                                    ...formData["team"]?.[0],
                                                    members: JSON.stringify(
                                                        ticketData.map(
                                                            singleTicketData =>
                                                                populateMissingFormData(
                                                                    singleTicketData,
                                                                    true
                                                                )
                                                        )
                                                    ),
                                                },
                                            ],
                                        },
                                    },
                                    true
                                )
                            );
                    } else {
                        return ticketData == null ||
                            ticketData.every(current =>
                                _.isEqual(current, ticketData[0])
                            )
                            ? () =>
                                  dispatch(
                                      addToCart(
                                          {
                                              id: ticketId,
                                              quantity: quantity,
                                              cart_item_data: {
                                                  item_data: [
                                                      populateMissingFormData(
                                                          formData[
                                                              ticketId
                                                          ]?.[0] ?? {}
                                                      ),
                                                  ],
                                              },
                                          },
                                          true
                                      )
                                  )
                            : Array.from({ length: getQuantity(ticketId) }).map(
                                  (_, i) => () =>
                                      dispatch(
                                          addToCart(
                                              {
                                                  id: ticketId,
                                                  quantity: 1,
                                                  cart_item_data: {
                                                      item_data: [
                                                          populateMissingFormData(
                                                              ticketData[i] ??
                                                                  {}
                                                          ),
                                                      ],
                                                  },
                                              },
                                              true
                                          )
                                      )
                              );
                    }
                });

            await promiseAllSequential(promises);
            setIsAddingTicketsSuccess(true);
            return true;
        } catch (error) {
            console.error(error);
            setAddingTicketsError(
                typeof error === "object" &&
                    error != null &&
                    "message" in error &&
                    typeof error.message === "string"
                    ? error.message
                    : "An issue occured adding tickets to the cart."
            );
            return false;
        } finally {
            setIsAddingTickets(false);
        }
    };

    return {
        additionalQuestions,
        tickets,
        teamTicketFromCart,
        selectedTeamTicketId,
        selectedTeamTicketSize,
        quantities,
        getQuantity,
        getTotalQuantity,
        incrementQuantity,
        decrementQuantity,
        resetQuantities,
        addTicketsToCart,
        isAddingTickets,
        isAddingTicketsSuccess,
        addingTicketsError,
        commentLabel,
        // isLoading,
        // isFetching,
        // isSuccess,
        // isError,
    };
};

export default useTicketsForPath;
